import { Card } from '@material-ui/core';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { Skeleton } from '@material-ui/lab';
import { Accordion } from '@monorepo/shared/componentsV2/Accordion';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { Select } from '@monorepo/shared/componentsV2/Select/Select';
import { focusOutline } from '@monorepo/shared/styles/focus';
import { fillHeightAndScrollable } from '@monorepo/shared/styles/layout';
import styled, { css } from 'styled-components';

const muiBreakpoints = createBreakpoints({});

/**
 * Month view scroll against the whole page.
 * Week and list views are contained scrolls within their containers.
 */
export const CalendarRoot = styled.div<{ $scrollsWithPage: boolean }>`
  /* FullCalendar default; week view uses the opaque mix for sticky headers */
  --fc-today-bg-color: rgb(255 220 40 / 15%);
  --today-bg-solid: color-mix(in srgb, rgb(255 220 40) 15%, white);

  display: flex;
  flex-direction: column;
  ${({ $scrollsWithPage }) =>
    $scrollsWithPage
      ? css`
          flex: 1 0 auto;
        `
      : css`
          flex: 1 1 0;
          min-height: 0;
        `}

  /* headerToolbar is empty (we render custom nav); drop its reserved space so
     the month grid sits directly below the header like the week view does */
  & .fc .fc-header-toolbar {
    display: none;
  }

  & .fc .fc-daygrid-day-number,
  & .fc-col-header-cell-cushion {
    /*  while the date numbers are not clickable, do not make them appear like they are */
    pointer-events: none;
  }

  & .fc-popover {
    /* don't cover our own modals with the more events popover */
    z-index: 2;
  }

  & .fc-more-popover {
    max-width: 220px;
  }

  & .fc-more-link:focus-visible {
    ${focusOutline()}
  }

  & .more-link-is-loading {
    display: none;
  }

  & .loading-indicator {
    /*  avoid hover on loading skeletons */
    pointer-events: none;
  }

  & .is-not-clickable {
    cursor: default;

    &:hover {
      background: none;
    }
  }

  /* our inner event link takes focus, so highlight the row like FullCalendar's hover */
  & .fc-daygrid-dot-event:has(a[href]:focus-visible) {
    background: rgb(0 0 0 / 10%);
  }
`;

export const CalendarHeaderContainer = styled.div`
  /* stays in view while month view scrolls the page */
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  /* padding (not margin) so the background covers the full stuck area */
  padding: 0.75rem 0;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const CalendarHeaderLayout = styled.div`
  display: grid;
  grid-template-areas:
    'left right'
    'datePicker datePicker';
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;

  ${muiBreakpoints.up('lg')} {
    grid-template-areas: 'left datePicker right';
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  }
`;

export const CalendarHeaderArea = styled.div<{
  $area: 'left' | 'datePicker' | 'right';
}>`
  display: flex;
  grid-area: ${({ $area }) => $area};
  gap: ${({ $area }) => ($area === 'datePicker' ? 0 : '0.75rem')};
  align-items: center;
  justify-self: ${({ $area }) =>
    ({ left: 'start', datePicker: 'center', right: 'end' }[$area])};
`;

export const ListDaySection = styled.section<{ $isPast?: boolean }>`
  opacity: ${({ $isPast }) => ($isPast ? 0.6 : 1)};
`;

export const ListDayHeaderDiv = styled.div`
  position: sticky;
  top: 0;
  z-index: 2; /* above the shared Accordion summary, which sets z-index 1 */
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.grayeee};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayddd};
`;

export const ListDayTitle = styled.h3`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 0;
`;

export const TodayChip = styled(Chip)`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.darkGreen};
`;

export const ListDaySummaryDiv = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  color: ${({ theme }) => theme.colors.blue};
`;

export const ListViewCard = styled(Card)`
  && {
    ${fillHeightAndScrollable}
  }
`;

// Month/Week select dropdown
export const ViewSelect = styled(Select)`
  width: 5rem;

  /* Menu is portaled by default; disablePortal is set in ViewSelect so these apply */
  & .MuiMenuItem-root {
    width: 10rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.builderBlue};

    &.Mui-focusVisible,
    &.Mui-selected {
      background-color: ${({ theme }) => theme.colors.builderBlue08};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.builderBlue16};
    }

    /* MUI's ripple defaults to currentColor, which is too dark on builder blue text */
    & .MuiTouchRipple-child {
      background-color: ${({ theme }) => theme.colors.builderBlue40};
    }
  }

  /* match the styling and height of the adjacent "Today" outlined primary button */
  & .MuiInputBase-root {
    height: 2.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.builderBlue};
    border-color: ${({ theme }) => theme.colors.builderBlue};
  }

  & .MuiInputBase-root:not(.Mui-disabled) {
    &.Mui-focused,
    &:hover {
      border-color: ${({ theme }) => theme.colors.builderBlue};
    }
  }

  /* color for the chevron v button */
  & .MuiSelect-icon {
    color: ${({ theme }) => theme.colors.builderBlue};
  }

  & .MuiFormHelperText-root:empty {
    display: none;
  }
`;

export const CalendarTitle = styled.h2`
  position: relative;
  display: flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  min-width: 13rem;
  margin: 0 0.5rem;
  white-space: nowrap;
`;

export const CalendarNavButton = styled(IconButton)`
  min-width: 2rem;
  font-size: 1rem;

  & svg {
    width: auto; /* to make sure both chevron-icons are the same height even though one is wider */
  }

  &.Mui-disabled {
    /* allow hovering over the button when it's disabled so the tooltip can be accessed */
    pointer-events: auto;

    & svg {
      fill: none;
    }
  }
`;

export const StyledCalendarEvent = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

export const StyledSkeleton = styled(Skeleton)`
  width: 95%;
  height: 32px;
  margin-bottom: 4px;
  margin-left: 4px;
  border-radius: 6px;
`;

export const WeekViewGridDiv = styled.div<{ $columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(props) => props.$columnCount},
    minmax(0, 1fr)
  );
  ${fillHeightAndScrollable}
  border: 1px solid ${({ theme }) => theme.colors.grayddd};
  border-radius: 6px;
`;

export const WeekDayColumnSection = styled.section<{ $isToday?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding-bottom: 0.5rem;

  /* solid so the sticky header (background: inherit) paints over scrolled events */
  background: ${({ $isToday, theme }) =>
    $isToday ? 'var(--today-bg-solid)' : theme.colors.white};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.grayddd};
  }

  & > *:not(:first-child) {
    padding-right: 0.25rem;
    padding-left: 0.25rem;
  }
`;

export const WeekDayHeaderDiv = styled.div`
  position: sticky;
  top: 0;
  z-index: 2; /* above the shared Accordion summary, which sets z-index 1 */
  padding: 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray666};
  text-align: center;
  background: inherit;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayddd};
`;

export const WeekDaySummaryDiv = styled.div`
  padding-top: 0.25rem;
  padding-bottom: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gray666};
  text-align: center;
`;

export const EmptySectionParagraph = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gray666};
`;

const CalendarEventLink = styled.a`
  display: block;
  color: ${({ theme }) => theme.colors.gray333};
  text-decoration: none;

  &[href]:hover {
    background-color: ${({ theme }) => theme.colors.grayeee};
  }

  &:focus-visible {
    ${focusOutline()}
  }
`;

export const ListViewEventLink = styled(CalendarEventLink)`
  padding: 0.5rem 0.75rem;
`;

export const MonthViewEventLink = styled(CalendarEventLink)`
  /* fill FullCalendar's flex event row, and shrink so the title can truncate */
  flex: 1;
  min-width: 0;

  /* FullCalendar's event row already highlights on hover */
  &[href]:hover {
    background-color: transparent;
  }
`;

export const WeekViewEventLink = styled(CalendarEventLink)`
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background-color: ${({ theme }) => theme.colors.grayf4};
  border: 1px solid ${({ theme }) => theme.colors.grayddd};
  border-radius: 6px;

  ${StyledCalendarEvent} {
    gap: 0.125rem;
    align-items: flex-start;
    overflow: visible;
  }
`;

export const InspectionsInfoIconContainer = styled('div')`
  display: flex;
  gap: 0.25rem;
  align-items: center;

  && > button svg {
    fill: ${({ theme }) => theme.colors.gray666};
  }

  ${muiBreakpoints.down('md')} {
    & > button {
      display: none;
    }
  }
`;

const CalendarSectionAccordion = styled(Accordion)`
  & .MuiAccordionDetails-root {
    padding: 0;
  }

  /* Disclosure caret sits to the left of a muted section label */
  & .MuiAccordionSummary-root,
  & .MuiAccordionSummary-root.Mui-expanded {
    flex-direction: row-reverse;
    gap: 0.25rem;
    justify-content: flex-end;
    min-height: unset;
    padding: 0;
  }

  & .MuiAccordionSummary-root:focus-visible,
  & .MuiAccordionSummary-root.Mui-focusVisible {
    ${focusOutline()}
  }

  & .MuiAccordionSummary-content,
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 0.25rem 0;
    font-size: 0.85rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.gray666};
  }

  & .MuiAccordionSummary-expandIcon {
    padding: 2px;
    margin: 0;
    color: ${({ theme }) => theme.colors.gray666};
  }
`;

export const WeekViewSectionAccordion = styled(CalendarSectionAccordion)`
  &&.MuiAccordion-root,
  && .MuiAccordionDetails-root,
  && .MuiAccordionSummary-root {
    background-color: transparent;
  }

  ${muiBreakpoints.down('md')} {
    && .MuiAccordionSummary-expandIcon {
      display: none;
    }
  }
`;

export const ListViewSectionAccordion = styled(CalendarSectionAccordion)`
  && .MuiAccordionSummary-root {
    padding-right: 0.75rem;
    padding-left: 0.75rem;
    background-color: ${({ theme }) => theme.colors.grayf4};
    border-top: 1px solid ${({ theme }) => theme.colors.grayeee};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayeee};
    border-radius: 0;
  }

  ${EmptySectionParagraph} {
    padding: 0.5rem 0.75rem;
  }
`;
